<template>
    <SettingsWrapper title="Pool settings" section="General">
        <v-alert v-if="!activeResourcePoolId" type="info" text>No resource pool is selected.</v-alert>
        <template v-else>
            <div class="d-flex align-center mb-6">
                <div>
                    <div class="text-h6">{{ activeResourcePool?.long_id || `Resource pool ${activeResourcePoolId}` }}</div>
                    <div class="caption text--secondary">ID {{ activeResourcePoolId }}</div>
                </div>
                <v-spacer></v-spacer>
                <v-btn icon :loading="refreshing" @click="refresh"><v-icon>mdi-refresh</v-icon></v-btn>
            </div>

            <v-alert v-if="!isResourcePoolManager" type="info" text>Resource pool details are available to managers only.</v-alert>
            <v-alert v-else-if="error" type="error" text dismissible @input="error = ''">{{ error }}</v-alert>

            <v-expansion-panels v-if="isResourcePoolManager" multiple flat>
                <v-expansion-panel>
                    <v-expansion-panel-header>
                        <div class="d-flex align-center">
                            <v-icon left>mdi-file-tree-outline</v-icon>
                            Hierarchy
                        </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                        <v-skeleton-loader v-if="loading.hierarchy" type="table"></v-skeleton-loader>
                        <v-data-table
                            v-else
                            :headers="hierarchyHeaders"
                            :items="hierarchy"
                            hide-default-footer
                            disable-pagination
                            no-data-text="No hierarchy data is available.">
                            <template v-slot:[`item.resource_pool_type`]="{ item }">
                                <v-chip small outlined>{{ item.resource_pool_type || '-' }}</v-chip>
                            </template>
                        </v-data-table>
                    </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                    <v-expansion-panel-header>
                        <div class="d-flex align-center">
                            <v-icon left>mdi-server-network-outline</v-icon>
                            Tenant cluster
                        </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                        <v-skeleton-loader v-if="loading.cluster" type="article"></v-skeleton-loader>
                        <template v-else-if="tenantCluster">
                            <v-row>
                                <v-col cols="12" md="4">
                                    <strong>Provider</strong>
                                    <div>{{ tenantCluster.cloud_provider || '-' }}</div>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <strong>Region</strong>
                                    <div>{{ tenantCluster.cloud_region || '-' }}</div>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <strong>Cluster</strong>
                                    <div>{{ tenantCluster.cluster_name || '-' }}</div>
                                </v-col>
                            </v-row>
                            <div class="d-flex flex-wrap justify-end">
                                <v-btn text color="primary" :loading="clusterAction === 'reconcile'" @click="reconcileCluster">Reconcile</v-btn>
                                <v-btn text color="warning" :loading="clusterAction === 'unlink'" @click="unlinkCluster">Unlink</v-btn>
                                <v-btn text color="error" :loading="clusterAction === 'teardown'" @click="teardownCluster">Teardown</v-btn>
                            </div>
                        </template>
                        <v-form v-else ref="clusterForm" v-model="validClusterBootstrap">
                            <v-row>
                                <v-col cols="12" md="6">
                                    <v-text-field
                                        v-model="clusterCredentials.id"
                                        label="OpenStack application credential ID"
                                        :rules="[required]"
                                        outlined
                                        dense></v-text-field>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-text-field
                                        v-model="clusterCredentials.secret"
                                        label="OpenStack application credential secret"
                                        type="password"
                                        :rules="[required]"
                                        outlined
                                        dense></v-text-field>
                                </v-col>
                            </v-row>
                            <div class="d-flex justify-end">
                                <v-btn color="primary" :loading="clusterAction === 'bootstrap'" :disabled="!validClusterBootstrap" @click="bootstrapCluster">
                                    Cloud account
                                </v-btn>
                            </div>
                        </v-form>
                    </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                    <v-expansion-panel-header>
                        <div class="d-flex align-center">
                            <v-icon left>mdi-memory</v-icon>
                            Node pools
                        </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                        <v-skeleton-loader v-if="loading.nodePools" type="table"></v-skeleton-loader>
                        <v-data-table
                            v-else
                            :headers="nodePoolHeaders"
                            :items="nodePools"
                            hide-default-footer
                            disable-pagination
                            no-data-text="No node pools are configured for this resource pool."></v-data-table>
                    </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                    <v-expansion-panel-header>
                        <div class="d-flex align-center">
                            <v-icon left>mdi-cash-multiple</v-icon>
                            Credits and prices
                        </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                        <v-row>
                            <v-col cols="12" lg="7">
                                <v-skeleton-loader v-if="loading.prices" type="table"></v-skeleton-loader>
                                <v-data-table
                                    v-else
                                    :headers="priceHeaders"
                                    :items="prices"
                                    hide-default-footer
                                    disable-pagination
                                    no-data-text="No pool-specific prices are configured."></v-data-table>
                            </v-col>
                            <v-col cols="12" lg="5">
                                <v-form ref="creditForm" v-model="validCredit">
                                    <v-text-field
                                        v-model.number="credit.amount"
                                        label="Credit amount"
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        :rules="[positive]"
                                        outlined
                                        dense></v-text-field>
                                    <v-text-field v-model="credit.comment" label="Transaction comment" :rules="[required]" outlined dense></v-text-field>
                                    <v-text-field
                                        v-model="credit.idempotencyKey"
                                        label="Idempotency key"
                                        :rules="[required, maxIdempotencyKeyLength]"
                                        outlined
                                        dense></v-text-field>
                                    <div class="d-flex justify-end">
                                        <v-btn color="primary" :loading="issuingCredit" :disabled="!validCredit" @click="issueCredit">Issue credit</v-btn>
                                    </div>
                                </v-form>
                            </v-col>
                        </v-row>
                    </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                    <v-expansion-panel-header>
                        <div class="d-flex align-center">
                            <v-icon left>mdi-delete-clock-outline</v-icon>
                            Deletion requests
                        </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                        <div class="d-flex justify-end mb-4">
                            <v-btn color="error" text :loading="creatingDeletionRequest" @click="createDeletionRequest">Request deletion</v-btn>
                        </div>
                        <v-skeleton-loader v-if="loading.deletionRequests" type="table"></v-skeleton-loader>
                        <v-data-table
                            v-else
                            :headers="deletionRequestHeaders"
                            :items="deletionRequests"
                            hide-default-footer
                            disable-pagination
                            no-data-text="There are no deletion requests.">
                            <template v-slot:[`item.actions`]="{ item }">
                                <v-btn v-if="isPendingDeletionRequest(item)" text small color="warning" @click="revokeDeletionRequest(item)">Revoke</v-btn>
                            </template>
                        </v-data-table>
                    </v-expansion-panel-content>
                </v-expansion-panel>
            </v-expansion-panels>
        </template>
    </SettingsWrapper>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import SettingsWrapper from '@/components/SettingsWrapper.vue'

export default {
    name: 'PoolSettingsGeneral',
    components: { SettingsWrapper },
    data() {
        return {
            refreshing: false,
            error: '',
            hierarchy: [],
            tenantCluster: null,
            nodePools: [],
            prices: [],
            deletionRequests: [],
            loading: { hierarchy: false, cluster: false, nodePools: false, prices: false, deletionRequests: false },
            clusterAction: '',
            validClusterBootstrap: false,
            validCredit: false,
            issuingCredit: false,
            creatingDeletionRequest: false,
            clusterCredentials: { id: '', secret: '' },
            credit: { amount: null, comment: '', idempotencyKey: '' },
            hierarchyHeaders: [
                { text: 'Name', value: 'long_id' },
                { text: 'Type', value: 'resource_pool_type' },
                { text: 'Pool ID', value: 'rpid' },
                { text: 'Root pool', value: 'root_rpid' },
                { text: 'Allocated', value: 'allocated_amount' },
                { text: 'Used', value: 'used_amount' }
            ],
            nodePoolHeaders: [
                { text: 'Name', value: 'long_id' },
                { text: 'Short ID', value: 'short_id' },
                { text: 'Cloud', value: 'cloud' },
                { text: 'Region', value: 'region' },
                { text: 'Enabled', value: 'enabled' }
            ],
            priceHeaders: [
                { text: 'Resource type', value: 'resource_type' },
                { text: 'Subresource', value: 'subresource' },
                { text: 'Denomination', value: 'denomination' },
                { text: 'Amount', value: 'amount' }
            ],
            deletionRequestHeaders: [
                { text: 'Requested', value: 'creation_timestamp' },
                { text: 'Status', value: 'status' },
                { text: '', value: 'actions', sortable: false, align: 'right' }
            ]
        }
    },
    computed: {
        ...mapState('userStore', ['selectedResourcePool', 'availableResourcePools']),
        ...mapGetters('userStore', ['isResourcePoolManager']),
        activeResourcePoolId() {
            return Number(this.$route.params.rpid || this.selectedResourcePool || this.availableResourcePools[0]?.rpid || 0)
        },
        activeResourcePool() {
            return this.availableResourcePools.find(pool => Number(pool.rpid) === this.activeResourcePoolId)
        }
    },
    methods: {
        required(value) {
            return Boolean(value) || 'Required'
        },
        positive(value) {
            return Number(value) > 0 || 'Enter an amount greater than zero'
        },
        maxIdempotencyKeyLength(value) {
            return String(value || '').length <= 255 || 'Maximum 255 characters'
        },
        notify(text, icon = 'check_circle') {
            this.$store.dispatch('showSnackBar', { snackBarText: text, snackBarTimeout: 5000, snackBarIcon: icon })
        },
        normalizeTenantClusters(tenantClusterResponse) {
            if (Array.isArray(tenantClusterResponse)) return tenantClusterResponse
            if (tenantClusterResponse && typeof tenantClusterResponse === 'object') return [tenantClusterResponse]
            return []
        },
        async refresh() {
            if (!this.activeResourcePoolId || !this.isResourcePoolManager) return
            this.refreshing = true
            this.error = ''
            try {
                await Promise.all([this.fetchHierarchy(), this.fetchCluster(), this.fetchNodePools(), this.fetchPrices(), this.fetchDeletionRequests()])
            } finally {
                this.refreshing = false
            }
        },
        flattenHierarchy(items, rows = []) {
            items.forEach(item => {
                const children = item.children || []
                rows.push({ ...item, children: undefined })
                this.flattenHierarchy(children, rows)
            })
            return rows
        },
        async fetchHierarchy() {
            this.loading.hierarchy = true
            try {
                const { data } = await this.$axios.get(`/resource_pools/${this.activeResourcePoolId}/hierarchy`)
                this.hierarchy = Array.isArray(data) ? this.flattenHierarchy(data) : []
            } catch (error) {
                this.error = error.response?.data?.debug || 'Could not load the resource pool hierarchy.'
            }
            this.loading.hierarchy = false
        },
        async fetchCluster() {
            this.loading.cluster = true
            try {
                const { data } = await this.$axios.get(`/resource_pools/${this.activeResourcePoolId}/tenant_cluster`)
                this.tenantCluster = this.normalizeTenantClusters(data)[0] || null
            } catch (error) {
                if (error.response?.status === 404) this.tenantCluster = null
                else this.error = error.response?.data?.debug || 'Could not load tenant cluster details.'
            }
            this.loading.cluster = false
        },
        async fetchNodePools() {
            this.loading.nodePools = true
            try {
                const { data } = await this.$axios.get(`/resource_pools/${this.activeResourcePoolId}/node_pools`)
                this.nodePools = Array.isArray(data) ? data : []
            } catch (error) {
                this.error = error.response?.data?.debug || 'Could not load node pools.'
            }
            this.loading.nodePools = false
        },
        async fetchPrices() {
            this.loading.prices = true
            try {
                const { data } = await this.$axios.get(`/resource_pools/${this.activeResourcePoolId}/prices`)
                this.prices = Array.isArray(data) ? data : []
            } catch (error) {
                this.error = error.response?.data?.debug || 'Could not load resource pool prices.'
            }
            this.loading.prices = false
        },
        async fetchDeletionRequests() {
            this.loading.deletionRequests = true
            try {
                const { data } = await this.$axios.get(`/resource_pools/${this.activeResourcePoolId}/deletion_requests`)
                this.deletionRequests = Array.isArray(data) ? data : []
            } catch (error) {
                this.error = error.response?.data?.debug || 'Could not load deletion requests.'
            }
            this.loading.deletionRequests = false
        },
        async bootstrapCluster() {
            if (!this.$refs.clusterForm.validate()) return
            this.clusterAction = 'bootstrap'
            try {
                await this.$axios.post(`/resource_pools/${this.activeResourcePoolId}/tenant_clusters`, {
                    os_app_credential_id: this.clusterCredentials.id,
                    os_app_credential_secret: this.clusterCredentials.secret
                })
                this.clusterCredentials = { id: '', secret: '' }
                this.notify('Cluster bootstrap has started.')
            } catch (error) {
                this.error = error.response?.data?.debug || 'Could not start cluster bootstrap.'
            }
            this.clusterAction = ''
        },
        reconcileCluster() {
            return this.performClusterAction('reconcile', 'reconcile', 'Cluster reconciliation has started.')
        },
        unlinkCluster() {
            return this.performClusterAction('unlink', 'unlink', 'Cluster unlinked.')
        },
        teardownCluster() {
            return this.performClusterAction('teardown', '', 'Cluster teardown has started.', 'delete')
        },
        async performClusterAction(action, suffix, successMessage, method = 'post') {
            if (!this.tenantCluster?.tcid) return
            this.clusterAction = action
            try {
                const path = `/resource_pools/${this.activeResourcePoolId}/tenant_clusters/${this.tenantCluster.tcid}${suffix ? `/${suffix}` : ''}`
                await this.$axios[method](path)
                this.notify(successMessage)
                await this.fetchCluster()
            } catch (error) {
                this.error = error.response?.data?.debug || `Could not ${action} the tenant cluster.`
            }
            this.clusterAction = ''
        },
        async issueCredit() {
            if (!this.$refs.creditForm.validate()) return
            this.issuingCredit = true
            try {
                await this.$axios.post(`/resource_pools/${this.activeResourcePoolId}/credits`, {
                    amount: this.credit.amount,
                    transaction_comment: this.credit.comment,
                    idempotency_key: this.credit.idempotencyKey
                })
                this.credit = { amount: null, comment: '', idempotencyKey: '' }
                this.$refs.creditForm.resetValidation()
                this.notify('Credit issued.')
            } catch (error) {
                this.error = error.response?.data?.debug || 'Could not issue credit.'
            }
            this.issuingCredit = false
        },
        async createDeletionRequest() {
            this.creatingDeletionRequest = true
            try {
                await this.$axios.post(`/resource_pools/${this.activeResourcePoolId}/deletion_requests`)
                this.notify('Deletion request created.')
                await this.fetchDeletionRequests()
            } catch (error) {
                this.error = error.response?.data?.debug || 'Could not create a deletion request.'
            }
            this.creatingDeletionRequest = false
        },
        isPendingDeletionRequest(request) {
            return !request.decision && !request.acceptance_timestamp && !request.revocation_timestamp
        },
        async revokeDeletionRequest(request) {
            const invitationId = request.invid || request.invitation_id
            if (!invitationId) return
            try {
                await this.$axios.post(`/resource_pools/${this.activeResourcePoolId}/deletion_requests/${invitationId}/revoke`)
                this.notify('Deletion request revoked.')
                await this.fetchDeletionRequests()
            } catch (error) {
                this.error = error.response?.data?.debug || 'Could not revoke the deletion request.'
            }
        }
    },
    watch: {
        activeResourcePoolId: {
            handler() {
                this.refresh()
            },
            immediate: true
        },
        isResourcePoolManager() {
            this.refresh()
        }
    }
}
</script>
